import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import Toast from "cogo-toast";
import {apiGetCheckTicket} from "./Actions";
import {DefaultLoader} from "../../../components/DefaultLoader";
import {fmtDateTime} from "../../../functions/formatDate";
import {Form} from "react-final-form";
import {Field} from "react-final-form";
import {FiEdit, FiImage, FiSearch} from "react-icons/fi";
import {Link} from "react-router-dom";
import EntranceTableV2 from "../EntranceV2/EntranceTableV2";
import ETable from "../components/Table";
import {getEnigooTurnstileTicketTicketType, getEnigooTurnstileTicketType} from "../enigooTurnstileHelper";
import PageHeader from "../components/PageHeader";
import {FilterButton} from "../components/Button";
import {StatusBadge} from "../components/Table";
import {CardHeader} from "../components/FormCard";
import {eventsById, campsById, resolveIds} from "./ticketDataConfig";

// ─── helpers ─────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');
function formatDate(raw) {
    if (!raw) return '—';
    let d;
    if (Array.isArray(raw)) {
        d = new Date(raw[0], raw[1] - 1, raw[2], raw[3] ?? 0, raw[4] ?? 0, raw[5] ?? 0);
    } else {
        d = new Date(raw);
    }
    if (isNaN(d)) return String(raw);
    return `${pad(d.getDate())}. ${pad(d.getMonth() + 1)}. ${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const CHANGE_TYPE_LABELS = {
    CHANGE_BC: 'Změna UID',
    STORNO: 'Storno',
    CHANGE_DATA: 'Změna dat',
    DELETE_IMAGE: 'Smazání fotky',
    BLOCK: 'Zablokování',
    UNBLOCK: 'Odblokování',
    CHIP_TRANSFER: 'Převod na jiný čip',
};

// ─── InfoField ────────────────────────────────────────────────────────────────

const InfoField = ({label, value}) => {
    if (value == null || value === "") return null;
    return (
        <div style={{display:"flex",flexDirection:"column",gap:"3px"}}>
            <span style={{fontSize: "var(--enigoo-fs-2xs)",fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</span>
            <span style={{fontSize: "var(--enigoo-fs-md)",color:"#1e293b",fontWeight:500,padding:"6px 10px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:"6px"}}>{String(value)}</span>
        </div>
    );
};

// ─── TicketDataView ───────────────────────────────────────────────────────────

const tagBase = {fontSize: "var(--enigoo-fs-xs)",fontWeight:600,borderRadius:"5px",padding:"3px 8px"};
const tagEvent = {...tagBase, background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe"};
const tagCamp  = {...tagBase, background:"#f0fdf4",color:"#16a34a",border:"1px solid #bbf7d0"};
const tagOther = {...tagBase, background:"#f8fafc",color:"#475569",border:"1px solid #e2e8f0"};

const TicketDataView = ({ticketData}) => {
    if (!ticketData) return null;
    let parsed;
    try {
        parsed = typeof ticketData === 'string' ? JSON.parse(ticketData) : ticketData;
    } catch {
        return (
            <pre style={{fontSize: "var(--enigoo-fs-xs)",color:"#475569",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:"6px",padding:"8px",margin:0,overflowX:"auto"}}>
                {ticketData}
            </pre>
        );
    }

    const allById = {...eventsById, ...campsById};
    const eventNames = resolveIds(parsed.events ?? parsed.event, allById);
    const campNames  = resolveIds(parsed.camp ?? parsed.camps,   allById);
    const knownKeys  = new Set(['events', 'event', 'camp', 'camps']);
    const unknownKeys = Object.keys(parsed).filter((k) => !knownKeys.has(k));

    if (eventNames.length === 0 && campNames.length === 0 && unknownKeys.length === 0) {
        return <span style={{fontSize: "var(--enigoo-fs-sm)",color:"#94a3b8"}}>Žádná data čipu</span>;
    }

    return (
        <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
            {eventNames.length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                    <span style={{fontSize: "var(--enigoo-fs-2xs)",fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>Nahrané události</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
                        {eventNames.map((name, i) => <span key={i} style={tagEvent}>{name}</span>)}
                    </div>
                </div>
            )}
            {campNames.length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                    <span style={{fontSize: "var(--enigoo-fs-2xs)",fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>Kemp</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
                        {campNames.map((name, i) => <span key={i} style={tagCamp}>{name}</span>)}
                    </div>
                </div>
            )}
            {unknownKeys.map((key) => (
                <div key={key} style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                    <span style={{fontSize: "var(--enigoo-fs-2xs)",fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>{key}</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>
                        <span style={tagOther}>{JSON.stringify(parsed[key])}</span>
                    </div>
                </div>
            ))}
        </div>
    );
};

// ─── main ─────────────────────────────────────────────────────────────────────

const CheckTicket = () => {
    const [data, setData]                 = useState(null);
    const [loading, setLoading]           = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showImage, setShowImage]       = useState(false);
    const user = useSelector(s => s.user);
    const bcRef = useRef(null);

    useEffect(() => {
        if (bcRef.current) { bcRef.current.focus(); bcRef.current.select(); }
    }, []);

    const checkTicket = (values) => {
        setLoading(true);
        setShowImage(false);
        apiGetCheckTicket(user.activeAreal.id, values.bc, (d) => {
            if (d) {
                setData(d);
                setErrorMessage(null);
            } else {
                Toast.error("Požadovaná vstupenka nebyla nalezena");
                setErrorMessage("Požadovaná vstupenka nebyla nalezena");
                setData(null);
            }
            setLoading(false);
        }, () => {
            Toast.error("Požadovaná vstupenka nebyla nalezena");
            setErrorMessage("Požadovaná vstupenka nebyla nalezena");
            setData(null);
            setLoading(false);
        });
    };

    return (
        <>
            {user.activeAreal && (
                <>
                    <PageHeader crumbs={[{label: "Kontrola vstupenky"}]}/>

                    {/* search form */}
                    <div style={{marginBottom:"14px",background:"#fff",border:"1px solid #e2e8f0",borderRadius:"8px",padding:"16px",boxShadow:"0 1px 3px rgba(0,0,0,0.05)"}}>
                        <Form onSubmit={checkTicket} render={({handleSubmit}) => (
                            <>
                                <div style={{position:"relative",marginBottom:"10px"}}>
                                    <FiSearch size={15} style={{position:"absolute",left:"12px",top:"50%",transform:"translateY(-50%)",color:"#94a3b8",pointerEvents:"none"}}/>
                                    <Field name="bc">
                                        {({input}) => (
                                            <input
                                                {...input}
                                                ref={bcRef}
                                                type="text"
                                                placeholder="Zadejte barcode vstupenky / karty..."
                                                onKeyDown={e => e.key === "Enter" && handleSubmit()}
                                                style={{width:"100%",padding:"9px 14px 9px 38px",border:"1px solid #cbd5e1",borderRadius:"8px",fontSize: "var(--enigoo-fs-md)",background:"#fff",color:"#1e293b",outline:"none",boxSizing:"border-box"}}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div style={{display:"flex",justifyContent:"flex-end"}}>
                                    <FilterButton
                                        type="button"
                                        onClick={handleSubmit}
                                        loading={loading}
                                        text="Vyhledat"
                                        icon={<FiSearch size={12}/>}
                                    />
                                </div>
                            </>
                        )}/>
                    </div>

                    {/* body */}
                    {loading ? (
                        <DefaultLoader text={"Hledám vstupenku..."}/>
                    ) : data ? (
                        <>
                            {/* ── ticket info card ── */}
                            <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginBottom:"14px",overflow:"hidden"}}>
                                <CardHeader
                                    title="Informace o vstupence"
                                    right={<>
                                        <StatusBadge active={data.active}/>
                                        <StatusBadge active={!data.blocked} activeLabel="Nezablokováno" inactiveLabel="Zablokováno"/>
                                        {(data.active && data.id) && (
                                            <Link to={"/ticket/detail/" + data.id}>
                                                <button style={{display:"flex",alignItems:"center",gap:"4px",padding:"4px 10px",background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:"6px",fontSize: "var(--enigoo-fs-xs)",fontWeight:600,cursor:"pointer"}}>
                                                    <FiEdit size={11}/> Detail vstupenky
                                                </button>
                                            </Link>
                                        )}
                                    </>}
                                />
                                <div style={{padding:"12px",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:"10px"}}>
                                    <InfoField label="BC"                       value={data.bc}/>
                                    <InfoField label="Platná od"                value={fmtDateTime(data.dateFrom)}/>
                                    <InfoField label="Platná do"                value={fmtDateTime(data.dateTo)}/>
                                    <InfoField label="Povolených průchodů"      value={data.entrance === 0 ? "∞" : data.entrance}/>
                                    <InfoField label="ID události"              value={data.eventId}/>
                                    <InfoField label="Sleva vstupenky"          value={getEnigooTurnstileTicketType(data.type)}/>
                                    <InfoField label="Typ vstupenky"            value={getEnigooTurnstileTicketTicketType(data.ticketType)}/>
                                    <InfoField label="Jméno"                    value={data.name}/>
                                    <InfoField label="Poznámka"                 value={data.note}/>
                                    <InfoField label="Tarif"                    value={data?.tariff?.name}/>
                                    {data.actualZone != null && (
                                        <InfoField label="Aktuální zóna" value={data.actualZone}/>
                                    )}
                                </div>
                            </div>

                            {/* ── image ── */}
                            {data.imageBase64 && (
                                <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginBottom:"14px",overflow:"hidden"}}>
                                    <CardHeader
                                        title={<span style={{display:"flex",alignItems:"center",gap:"6px"}}><FiImage size={13} style={{color:"#64748b"}}/>Fotka vstupenky</span>}
                                        right={
                                            <button type="button" onClick={() => setShowImage(v => !v)}
                                                style={{display:"flex",alignItems:"center",gap:"4px",padding:"4px 10px",background: showImage ? "#f1f5f9" : "#eff6ff",color: showImage ? "#475569" : "#2563eb",border:`1px solid ${showImage ? "#e2e8f0" : "#bfdbfe"}`,borderRadius:"6px",fontSize: "var(--enigoo-fs-xs)",fontWeight:600,cursor:"pointer"}}
                                            >
                                                {showImage ? "Skrýt fotku" : "Zobrazit fotku"}
                                            </button>
                                        }
                                    />
                                    {showImage && (
                                        <div style={{padding:"12px",display:"flex",justifyContent:"center"}}>
                                            <img
                                                src={data.imageBase64}
                                                alt="Fotka vstupenky"
                                                style={{maxWidth:"100%",maxHeight:"400px",borderRadius:"8px",border:"1px solid #e2e8f0"}}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── ticketData ── */}
                            {data.ticketData && (
                                <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginBottom:"14px",overflow:"hidden"}}>
                                    <CardHeader title="Data čipu"/>
                                    <div style={{padding:"12px"}}>
                                        <TicketDataView ticketData={data.ticketData}/>
                                    </div>
                                </div>
                            )}

                            {/* ── entrances ── */}
                            <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginBottom:"14px",overflow:"hidden"}}>
                                <CardHeader title={`Průchody (${data?.entrances?.length ?? 0})`}/>
                                <div style={{padding:"12px"}}>
                                    <EntranceTableV2 data={data?.entrances ?? []}/>
                                </div>
                            </div>

                            {/* ── changeLogs ── */}
                            {data.changeLogs && data.changeLogs.length > 0 && (
                                <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginBottom:"14px",overflow:"hidden"}}>
                                    <CardHeader title={`Historie změn (${data.changeLogs.length})`}/>
                                    <ETable
                                        data={data.changeLogs}
                                        columns={[
                                            {header: "Datum",  render: log => formatDate(log.date)},
                                            {header: "Typ",    render: log => (
                                                <span style={{fontSize: "var(--enigoo-fs-xs)",fontWeight:600,borderRadius:"4px",padding:"2px 7px",background:"#f1f5f9",color:"#475569",border:"1px solid #e2e8f0"}}>
                                                    {CHANGE_TYPE_LABELS[log.type] ?? log.type ?? "—"}
                                                </span>
                                            )},
                                            {header: "Admin",  render: log => log.adminName ? `${log.adminName} (${log.adminEmail})` : log.adminEmail ?? "—"},
                                        ]}
                                    />
                                </div>
                            )}
                        </>
                    ) : errorMessage ? (
                        <div style={{padding:"32px",textAlign:"center",color:"#dc2626",fontSize: "var(--enigoo-fs-md)",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:"8px"}}>
                            {errorMessage}
                        </div>
                    ) : null}
                </>
            )}
        </>
    );
};

export default CheckTicket;
